import { useState } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSTabButton from '../../../design-system/DSTabButton';
import KlydoMap from '../../KlydoMap/components/KlydoMap';
import AnalyticsTab from '../../Collections/components/AnalyticsTab';
import { OrderGraphPeriod } from '../../../api/collections';
import useAllCollectionsStatsQuery from '../../Collections/hooks/useAllCollectionsStatsQuery';
import useAllCollectionsCountryPerformanceQuery from '../../Collections/hooks/useAllCollectionsCountryPerformanceQuery';
import useAllCollectionsOrdersGraphQuery from '../../Collections/hooks/useAllCollectionsOrdersGraphQuery';

enum AnalyticsTabs {
  KlydoMap = 'klydo-map',
  CollectionAnalytics = 'collection-analytics',
}

const analyticsTabLabels: Record<AnalyticsTabs, string> = {
  [AnalyticsTabs.KlydoMap]: 'Klydo Map',
  [AnalyticsTabs.CollectionAnalytics]: 'Collection Analytics',
};

const analyticsTabs = [
  AnalyticsTabs.KlydoMap,
  AnalyticsTabs.CollectionAnalytics,
];

const AnalyticsSection = () => {
  const { spacing } = useTheme();
  const [activeTab, setActiveTab] = useState<AnalyticsTabs>(
    AnalyticsTabs.KlydoMap,
  );
  const [selectedPeriod, setSelectedPeriod] = useState<OrderGraphPeriod>(
    OrderGraphPeriod.WEEK,
  );

  const {
    data: statsData,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useAllCollectionsStatsQuery(selectedPeriod);
  const {
    data: countryPerformanceData,
    isLoading: isCountryPerformanceLoading,
    isError: isCountryPerformanceError,
  } = useAllCollectionsCountryPerformanceQuery(selectedPeriod);
  const {
    data: ordersGraphData,
    isLoading: isOrdersGraphLoading,
    isError: isOrdersGraphError,
  } = useAllCollectionsOrdersGraphQuery(selectedPeriod);

  const isAnalyticsLoading =
    isStatsLoading || isCountryPerformanceLoading || isOrdersGraphLoading;

  return (
    <>
      <DSBox
        sx={{
          display: 'flex',
          gap: spacing(1),
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxWidth: {
            xxs: '100%',
            xs: '50%',
            sm: '60%',
            md: '70%',
            lg: '80%',
          },
          paddingBottom: spacing(0.5),
          '&::-webkit-scrollbar': {
            height: '4px',
          },
        }}
      >
        {analyticsTabs.map((tab) => (
          <DSTabButton
            key={tab}
            title={analyticsTabLabels[tab]}
            isSelected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          />
        ))}
      </DSBox>
      {activeTab === AnalyticsTabs.KlydoMap && <KlydoMap />}
      {activeTab === AnalyticsTabs.CollectionAnalytics && (
        <AnalyticsTab
          statsData={{
            totalPurchases: statsData?.totalOrders || 0,
            totalSales: statsData?.totalSales || 0,
          }}
          countryPerformanceData={countryPerformanceData || []}
          orderGraphData={ordersGraphData?.graphData || []}
          totalOrders={ordersGraphData?.totalOrders || 0}
          layout="column"
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          isLoading={isAnalyticsLoading}
          statsError={isStatsError}
          countryPerformanceError={isCountryPerformanceError}
          orderGraphError={isOrdersGraphError}
        />
      )}
    </>
  );
};

export default AnalyticsSection;
